// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general functions for shared functionality.
import { setCookie, getCookie, deleteCookie } from '../functions.js';
import constants from '../constants.js';

// Import axios for API consuming in a clean interface.
import axios from 'axios';

import Swiper, { Pagination, Navigation } from 'swiper';

Swiper.use([Pagination, Navigation]);

const defaultApp = {
    mixins: [mixin],
    data() {
        return {
            dataApi: '',
            dataLimit: 15,
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataWidget: false,
            dataOverruleIds: [],
            dataCulture: 'nl-NL',
            loading: false,
            loadingMore: false,
            debounceSetLoading: null,
            debouncedFetch: null,
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            blockView: true,
            page: 1,
            pageLimit: 100,
            searchText: '',
            searchQuery: '',
            filterParams: null,
            error: false,

            constants: constants,
        }
    },
    // Watcher for pushing view preference to cookie for returning visitors.
    watch: {
        blockView(newView) {
            if (!newView) {
                setCookie(this.constants.cookies.listBlockView, 'false', 365);
            } else {
                deleteCookie(this.constants.cookies.listBlockView);
            }
        },
        searchText(...args) {
            this.debouncedFetch(...args);
        },
        searchQuery(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.page = 1;
                this.fetchData(true);
            }
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets.length;
        },
        itemsSet() {
            return this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet && this.data.facets.some(facet => facet.values.length)) || this.dataOverruleIds.length;
        },
        filtersActive() {
            return (this.facetsSet && this.data.facets.some(facet => facet.selected)) || this.searchQuery;
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection || this.searchText) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            if (this.filterParams) {
                return Array.from(this.filterParams.entries()).map(([key, value]) => ({
                    Key: key,
                    Values: value.split(','),
                    Operator: constants.operators.in,
                }));
            }
            if (this.dataOverruleIds.length) {
                return [
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in,
                    }
                ]
            }
            return this.data.facets.map(facet => {
                if (facet.values.length) {
                    return {
                        Key: facet.alias,
                        Values: facet.values,
                        Operator: this.constants.operators.in,
                    }
                }
            }).filter(item => item);
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.itemsSet && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchQuery
            }
        },
        amountsSentence() {
            return this.dataAmountsSentence
                .replace(this.constants.magicStrings.queryCount, this.data.items.length)
                .replace(this.constants.magicStrings.totalCount, this.data.queryCount);
        },
    },
    methods: {
        async init() {
            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget;
            if (this.$el.dataset.overruleIds) this.dataOverruleIds = this.$el.dataset.overruleIds.split(',');
            if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.dataCulture;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortKey) this.dataSortKey = this.$el.dataset.sortKey;
            if (this.$el.dataset.sortDirection) this.dataSortDirection = this.$el.dataset.sortDirection;
            if (this.$el.dataset.amountsSentence) this.dataAmountsSentence = this.$el.dataset.amountsSentence;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam > this.pageLimit) pageParam = this.pageLimit;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            this.filterParams = filterParams;

            // The code below is needed when facets have different operators.
            // Preset values for facets based on query params.
            //if (filterParams) {
            //    const facetsResponse = await this.fetchData();
            //    this.setFacets(facetsResponse);
            //    this.setFacetValuesFromUrl(filterParams);
            //}

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS.
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            let tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData();
            let replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;
            this.filterParams = null;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }

            this.$nextTick(() => {
                if (this.$refs.slider) {
                    new Swiper(this.$refs.slider, {
                        spaceBetween: 32,
                        slidesPerView: 1.2,
                        breakpoints: {
                            768: {
                                slidesPerView: 2.2
                            },
                            1024: {
                                slidesPerView: 2.7
                            },
                            1407: {
                                slidesPerView: 3.4
                            },
                        },
                        navigation: {
                            nextEl: this.$refs.next,
                            prevEl: this.$refs.prev,
                        },
                        pagination: {
                            el: this.$refs.pagination,
                            clickable: true,
                            type: 'fraction'
                        }
                    });
                }
            });
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        async fetchData(loading = true) {
            if (loading) this.debounceSetLoading(true);
            let response = null;
            try {
                response = await axios.post(this.dataApi, this.queryModel);
            } catch (error) {
                this.error = true;
                console.log(error);
            } finally {
                if (loading) this.debounceSetLoading(false);
            }
            return response;
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData() {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore() {
            this.loadingMore = true;
            this.page++;
            const response = await this.fetchData(false);
            this.setData(response);
            this.loadingMore = false;
            this.updateUrl();
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            }
            else {
                response.data.items.forEach(item => {
                    this.data.items.push(item);
                })
            }
            this.setFacets(response);
        },
        // The code below is needed when facets have different operators.
        // Method to set facet values based on URL search params.
        //setFacetValuesFromUrl(filterParams) {
        //    if (!this.facetsSet) return;
        //    this.data.facets.forEach(facet => {
        //        if (filterParams.has(facet.alias)) {
        //            let valueArray = filterParams.get(facet.alias).split(',');
        //            facet.values = valueArray
        //                .map(alias => facet.options.find(option => option.valueAlias === alias))
        //                .map(option => option.valueAlias);
        //        }
        //    });
        //},
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(facet => ({
                ...facet,
                values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
            }));
            this.data.queryCount = response.data.queryCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        // Data alteration function for reloading data with set filters.
        toggleOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (changedFacet && changedFacet.values.includes(optionAlias)) {
                changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
            } else {
                changedFacet.values.push(optionAlias);
            }
            this.fetchInitialData();
        },
        selectOption(facetAlias, event) {
            let optionAlias = event.target.value;
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (optionAlias === '') {
                changedFacet.values = [];
            } else {
                changedFacet.values = [optionAlias];
            }
            this.fetchInitialData();
        },
        removeSelect(facetAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            changedFacet.values = [];
            this.page = 1;
            this.fetchInitialData();
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach(filterItem => urlParams.append(filterItem.Key, filterItem.Values.join()))
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        createDebouncedLoading() {
            return this.debounce((state) => {
                this.loading = state;
            }, 500);
        },
    },
    created() {
        // Check which view is set as cookie.
        if (getCookie(this.constants.cookies.listBlockView) !== "") this.blockView = false;
        // Initialize the debounced loading function.
        this.debounceSetLoading = this.createDebouncedLoading();

        this.debouncedFetch = this.debounce((newValue, oldValue) => {
            if (newValue.length > 2) {
                this.searchQuery = newValue;
            } else {
                this.searchQuery = '';
            }
            this.fetchInitialData();
        });
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    },
};

export default defaultApp;
